// Filter controls for the Invoices tab (CHO-761): a multi-select unit filter
// (all units selected by default; invoices without a property group under an
// explicit "Unassigned" option), a payment-status filter covering all four
// statuses (all on by default), and the excluded-rows toggle with its count.
//
// Prop-driven: no fetching, no filter state of its own — the page owns the
// data and the selection state and passes everything down.

import type { InvoicePaymentStatus } from '../../types/invoices';
import { ALL_STATUSES, type UnitOption } from '../../lib/invoices';

export type { UnitOption };

const STATUS_LABELS: Record<InvoicePaymentStatus, string> = {
  unpaid: 'Unpaid',
  paid: 'Paid',
  failed: 'Failed',
  unknown: 'Unknown',
};

interface InvoiceFilterBarProps {
  unitOptions: UnitOption[];
  /** Keys the operator has unchecked — empty means every unit is selected. */
  deselectedUnitKeys: ReadonlySet<string>;
  onToggleUnit: (key: string) => void;
  /** Statuses the operator has unchecked — empty means all four are selected. */
  deselectedStatuses: ReadonlySet<InvoicePaymentStatus>;
  onToggleStatus: (status: InvoicePaymentStatus) => void;
  /** Excluded rows matching the current filters — what the toggle would reveal. */
  excludedCount: number;
  showExcluded: boolean;
  onToggleShowExcluded: () => void;
}

function FilterChip({
  checked,
  onToggle,
  label,
}: {
  checked: boolean;
  onToggle: () => void;
  label: string;
}) {
  return (
    <label
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border cursor-pointer select-none ${
        checked
          ? 'bg-gray-900 text-white border-gray-900'
          : 'bg-white text-gray-500 border-gray-200 hover:border-gray-300'
      }`}
    >
      <input type="checkbox" checked={checked} onChange={onToggle} className="sr-only" />
      <span aria-hidden="true" className="text-[10px] leading-none">{checked ? '✓' : '○'}</span>
      {label}
    </label>
  );
}

export function InvoiceFilterBar({
  unitOptions,
  deselectedUnitKeys,
  onToggleUnit,
  deselectedStatuses,
  onToggleStatus,
  excludedCount,
  showExcluded,
  onToggleShowExcluded,
}: InvoiceFilterBarProps) {
  return (
    <div className="space-y-3 mb-4">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs font-medium text-gray-400 uppercase tracking-wide mr-1">Unit</span>
        {unitOptions.map((unit) => (
          <FilterChip
            key={unit.key}
            label={unit.label}
            checked={!deselectedUnitKeys.has(unit.key)}
            onToggle={() => onToggleUnit(unit.key)}
          />
        ))}
        {unitOptions.length === 0 && <span className="text-xs text-gray-400">No units</span>}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs font-medium text-gray-400 uppercase tracking-wide mr-1">Status</span>
        {ALL_STATUSES.map((status) => (
          <FilterChip
            key={status}
            label={STATUS_LABELS[status]}
            checked={!deselectedStatuses.has(status)}
            onToggle={() => onToggleStatus(status)}
          />
        ))}
        <button
          type="button"
          onClick={onToggleShowExcluded}
          aria-pressed={showExcluded}
          className={`ml-auto inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${
            showExcluded
              ? 'bg-gray-100 text-gray-700 border-gray-300 hover:bg-gray-200'
              : 'bg-white text-gray-500 border-gray-200 hover:border-gray-300'
          }`}
        >
          <span aria-hidden="true" className="text-[10px] leading-none">{showExcluded ? '👁' : '○'}</span>
          {excludedCount} excluded — {showExcluded ? 'hide' : 'show'}
        </button>
      </div>
    </div>
  );
}
